import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  ConfirmModalComponent,
  InfoBannerComponent,
  ModalComponent,
  SegmentedControlComponent,
  SegmentedControlItem,
  SpinnerComponent,
  TagVariant,
} from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { StripeAdapter } from '../../adapters/stripe.adapter';
import { AppState } from '../../app.reducer';
import {
  FirstBookingPaymentMethod,
  StripeBillingInterval,
  StripeProductType,
} from '../../enums/stripe.enum';
import { StripeProduct } from '../../interfaces/stripe';
import { ScheduledPlanChange } from '../../interfaces/subscription';
import { BookingConfirmationService } from '../../services/booking-confirmation.service';
import { selectSubscription } from '../../store/subscription/subscription.reducer';
import { isImmediatePlanChange } from '../../utils/is-immediate-plan-change.util';
import { translateBillingPeriod } from '../../utils/billing-period-display.util';
import {
  filterBillingIntervalOptions,
  resolveBookingInterval,
  siblingPackageInterval,
} from '../../utils/package-billing-interval.util';
import {
  AgentsPlanSelectorComponent,
  CUSTOM_AGENTS_OPTION,
} from '../booking-selectors/agents-plan-selector/agents-plan-selector.component';
import { ButtonComponent } from '../button/button.component';
import { InvoiceBookingConfirmModalComponent } from '../invoice-booking-confirm-modal/invoice-booking-confirm-modal.component';
import { PaymentCheckoutComponent } from '../payment-checkout/payment-checkout.component';
import { PaymentMethodChoiceComponent } from '../payment-method-choice/payment-method-choice.component';

@Component({
  selector: 'app-agents-booking-modal',
  imports: [
    CommonModule,
    TranslocoModule,
    ModalComponent,
    PaymentCheckoutComponent,
    PaymentMethodChoiceComponent,
    ButtonComponent,
    SegmentedControlComponent,
    InfoBannerComponent,
    SpinnerComponent,
    InvoiceBookingConfirmModalComponent,
    ConfirmModalComponent,
    AgentsPlanSelectorComponent,
  ],
  templateUrl: './agents-booking-modal.component.html',
  styleUrl: './agents-booking-modal.component.scss',
})
export class AgentsBookingModalComponent {
  private readonly modal = viewChild<ModalComponent>('modal');
  public readonly paymentCheckout = viewChild(PaymentCheckoutComponent);
  private readonly paymentMethodChoice =
    viewChild<PaymentMethodChoiceComponent>('paymentMethodChoice');
  private readonly invoiceConfirmModal =
    viewChild.required<InvoiceBookingConfirmModalComponent>('invoiceConfirm');
  private readonly downgradeConfirmModal = viewChild<ConfirmModalComponent>(
    'downgradeConfirmModal',
  );
  private readonly cancelScheduleConfirmModal =
    viewChild<ConfirmModalComponent>('cancelScheduleConfirmModal');
  private readonly stripeAdapter = inject(StripeAdapter);
  private readonly store = inject(Store<AppState>);
  private readonly toastsService = inject(ToastsService);
  private readonly transloco = inject(TranslocoService);
  private readonly bookingConfirmation = inject(BookingConfirmationService);
  public agentsBooked = output<{ agents: number }>();

  private readonly allBillingIntervalOptions: ReadonlyArray<
    SegmentedControlItem<StripeBillingInterval>
  > = [
    {
      label: 'company-tool.snippets.agents-booking-modal.interval-month',
      value: StripeBillingInterval.MONTH,
    },
    {
      label: 'company-tool.snippets.agents-booking-modal.interval-year',
      value: StripeBillingInterval.YEAR,
      tag: {
        label: 'company-tool.snippets.agents-booking-modal.interval-year-save',
        type: TagVariant.GREEN,
      },
    },
  ];

  public readonly CUSTOM_OPTION_VALUE = CUSTOM_AGENTS_OPTION;

  public isPaymentStep = signal(false);
  public isPaymentMethodStep = signal(false);
  public selfBookingAddons = signal<StripeProduct[]>([]);
  public readonly selfBookingAddonsLoaded = signal(false);
  public billingInterval = signal(StripeBillingInterval.MONTH);
  public onPaymentCompletedFunction = signal(() => {});
  public bookingInProgress = signal(false);
  public cancelScheduleBusy = signal(false);
  public selectedOptionValue = signal<string | null>(null);
  public customAgentCount = signal(1);
  public readonly selectionLocked = signal(false);

  private readonly subscription = this.store.selectSignal(selectSubscription);
  private pendingCheckoutSessionId: string | null = null;
  private pendingInvoiceAddon: StripeProduct | null = null;
  pendingDowngradeAddon: StripeProduct | null = null;
  private pendingQuantity = 1;
  private catalogRequestId = 0;
  private readonly catalogCache = new Map<
    StripeBillingInterval,
    StripeProduct[]
  >();

  readonly singleSeatAddon = computed(
    (): StripeProduct | null =>
      this.selfBookingAddons().find((addon) => this.maxAgentsOf(addon) === 1) ??
      null,
  );

  readonly isUpdating = computed(
    () => this.bookingInProgress() && !this.isPaymentStep(),
  );

  readonly agentsSubscription = computed(
    () => this.subscription()?.subscriptionSummary?.agentsSubscription ?? null,
  );

  readonly hasExistingAgentsSubscription = computed(
    () => !!this.agentsSubscription()?.name,
  );

  readonly currentAgentsInterval = computed(
    (): StripeBillingInterval | null => {
      const interval = this.agentsSubscription()?.interval;
      if (interval === StripeBillingInterval.YEAR) {
        return StripeBillingInterval.YEAR;
      }
      if (interval === StripeBillingInterval.MONTH) {
        return StripeBillingInterval.MONTH;
      }
      return null;
    },
  );

  readonly currentPriceId = computed(
    () => this.agentsSubscription()?.priceId ?? null,
  );

  readonly currentQuantity = computed(
    () => Math.max(1, this.agentsSubscription()?.quantity ?? 1),
  );

  /**
   * Interval of other paid package items. Mixed intervals cannot be booked here.
   * @returns {StripeBillingInterval | null}
   */
  readonly siblingInterval = computed(() =>
    siblingPackageInterval(
      this.subscription()?.subscriptionSummary,
      StripeProductType.AGENTS,
    ),
  );

  /**
   * Month/year tabs, without intervals locked by a sibling package item.
   * @returns {ReadonlyArray<SegmentedControlItem<StripeBillingInterval>>}
   */
  readonly billingIntervalOptions = computed(() =>
    filterBillingIntervalOptions(
      this.allBillingIntervalOptions,
      this.siblingInterval(),
    ),
  );

  readonly currentMaxAgents = computed(
    () => this.agentsSubscription()?.maxAgents ?? 0,
  );

  readonly viewingCurrentInterval = computed(
    () => this.billingInterval() === this.currentAgentsInterval(),
  );

  readonly isCustomCurrent = computed(() => {
    const seat = this.singleSeatAddon();
    return (
      !!seat &&
      this.viewingCurrentInterval() &&
      seat.id === this.currentPriceId() &&
      this.customAgentCount() === this.currentQuantity()
    );
  });

  readonly scheduledAgentsChange = computed((): ScheduledPlanChange | null => {
    const changes =
      this.subscription()?.subscriptionSummary?.scheduledPlanChanges ?? [];
    return (
      changes.find(
        (change) => change.productType === StripeProductType.AGENTS,
      ) ?? null
    );
  });

  readonly canCancelSchedule = computed(() => {
    const actions =
      this.subscription()?.subscriptionSummary?.planActions?.[
        StripeProductType.AGENTS
      ];
    return !!actions?.canCancelSchedule && !!this.scheduledAgentsChange();
  });

  readonly downgradeEffectiveDate = computed(() => {
    const agents = this.agentsSubscription();
    return (
      agents?.validUntil ??
      agents?.nextBillingDate ??
      agents?.cancelAtIfNow ??
      null
    );
  });

  readonly selectedAddon = computed(() => {
    const selected = this.selectedOptionValue();
    if (!selected || selected === this.CUSTOM_OPTION_VALUE) return null;
    return (
      this.selfBookingAddons().find((addon) => addon.id === selected) ?? null
    );
  });

  readonly currentPackagePriceId = computed(() => {
    const priceId = this.currentPriceId();
    if (!priceId || !this.viewingCurrentInterval()) return null;
    if (this.singleSeatAddon()?.id === priceId) return null;
    return this.selfBookingAddons().some((addon) => addon.id === priceId)
      ? priceId
      : null;
  });

  readonly confirmLabelKey = computed(() => {
    if (!this.hasExistingAgentsSubscription()) {
      return 'company-tool.snippets.agents-booking-modal.book-button';
    }
    const custom = this.selectedOptionValue() === this.CUSTOM_OPTION_VALUE;
    const seat = this.singleSeatAddon();
    const addon = this.selectedAddon();
    const isCurrent = custom
      ? this.isCustomCurrent()
      : !!addon && this.isCurrentPlan(addon);
    const immediate = custom
      ? !seat ||
        this.isImmediate(
          this.customAgentCount(),
          seat.unit_amount * this.customAgentCount(),
        )
      : !addon ||
        this.isImmediate(this.maxAgentsOf(addon), addon.unit_amount);
    return isCurrent || immediate
      ? 'company-tool.snippets.agents-booking-modal.confirm-upgrade'
      : 'company-tool.snippets.agents-booking-modal.confirm-downgrade';
  });

  readonly modalTitleKey = computed(() => {
    if (this.isPaymentMethodStep()) {
      return 'company-tool.snippets.payment-method-choice.title';
    }
    return this.hasExistingAgentsSubscription()
      ? 'company-tool.snippets.agents-booking-modal.title-change'
      : 'company-tool.snippets.agents-booking-modal.title';
  });

  readonly canBook = computed(() => {
    if (this.bookingInProgress()) return false;
    if (this.selectedOptionValue() === this.CUSTOM_OPTION_VALUE) {
      return (
        !!this.singleSeatAddon() &&
        this.customAgentCount() > 0 &&
        !this.isCustomCurrent()
      );
    }
    const addon = this.selectedAddon();
    return !!addon && !this.isCurrentPlan(addon);
  });

  /**
   * Opens the modal on the billing tab of the current Agents sub,
   * or the sibling package interval when monthly cannot be mixed.
   * @returns {void}
   */
  public open(): void {
    const currentInterval = resolveBookingInterval({
      ownInterval: this.currentAgentsInterval(),
      siblingInterval: this.siblingInterval(),
      fallback: StripeBillingInterval.YEAR,
    });
    this.selectedOptionValue.set(null);
    this.customAgentCount.set(1);
    this.selectionLocked.set(false);
    this.catalogCache.clear();
    this.catalogRequestId++;
    this.billingInterval.set(currentInterval);
    this.isPaymentStep.set(false);
    this.isPaymentMethodStep.set(false);
    this.bookingInProgress.set(false);
    this.loadProducts();
    this.modal().open();
  }

  private close(): void {
    this.modal().close();
  }

  public setBillingInterval(interval: StripeBillingInterval): void {
    if (
      this.bookingInProgress() ||
      this.selectionLocked() ||
      this.billingInterval() === interval
    ) {
      return;
    }
    const lockedInterval = this.siblingInterval();
    if (lockedInterval && interval !== lockedInterval) return;
    this.billingInterval.set(interval);
    const cached = this.catalogCache.get(interval);
    if (cached) {
      this.applyCatalog(interval, cached);
      return;
    }
    this.loadProducts();
  }

  /**
   * Reads max_agents from price or product metadata.
   * @param {StripeProduct} addon Catalog price
   * @returns {number}
   */
  public maxAgentsOf(addon: StripeProduct): number {
    const fromPrice = +(addon.metadata?.max_agents ?? 0);
    if (fromPrice > 0) return fromPrice;
    return +(addon.product?.metadata?.max_agents ?? 0);
  }

  /**
   * Whether a package card is the live Agents plan.
   * @param {StripeProduct} addon Catalog price
   * @returns {boolean}
   */
  public isCurrentPlan(addon: StripeProduct): boolean {
    return this.viewingCurrentInterval() && addon.id === this.currentPriceId();
  }

  /**
   * Books the selected custom quantity or package.
   * @returns {void}
   */
  public bookSelected(): void {
    if (!this.canBook()) return;
    const custom = this.selectedOptionValue() === this.CUSTOM_OPTION_VALUE;
    const addon = custom ? this.singleSeatAddon() : this.selectedAddon();
    if (!addon) return;
    this.confirmOrBook(addon, custom ? this.customAgentCount() : 1);
  }

  /**
   * Applies a confirmed scheduled downgrade.
   * @returns {void}
   */
  public confirmDowngrade(): void {
    const addon = this.pendingDowngradeAddon;
    const quantity = this.pendingQuantity;
    this.pendingDowngradeAddon = null;
    this.selectionLocked.set(false);
    if (!addon) return;
    this.bookAddon(addon, quantity);
  }

  public openCancelScheduleConfirm(): void {
    if (this.cancelScheduleBusy() || this.isUpdating()) return;
    this.cancelScheduleConfirmModal()?.modal.open();
  }

  public cancelScheduledChange(): void {
    if (this.cancelScheduleBusy()) return;
    this.cancelScheduleBusy.set(true);
    this.stripeAdapter
      .cancelScheduledPlanChange(StripeProductType.AGENTS)
      .subscribe({
        next: () => {
          this.cancelScheduleBusy.set(false);
          this.toastsService.info(
            'company-tool.snippets.agents-booking-modal.schedule-canceled',
          );
          this.agentsBooked.emit({ agents: this.currentMaxAgents() });
        },
        error: () => {
          this.cancelScheduleBusy.set(false);
          this.toastsService.alert(
            'company-tool.views.settings.company.payment.sections.subscriptions.action-error',
          );
        },
      });
  }

  public backToSelection(): void {
    if (
      this.isPaymentStep() &&
      this.bookingConfirmation.needsPaymentMethodChoice()
    ) {
      this.paymentCheckout()?.destroy();
      this.isPaymentStep.set(false);
      this.isPaymentMethodStep.set(true);
      this.bookingInProgress.set(false);
      this.pendingCheckoutSessionId = null;
      return;
    }
    if (this.isPaymentStep()) {
      this.paymentCheckout()?.destroy();
    }
    this.isPaymentStep.set(false);
    this.isPaymentMethodStep.set(false);
    this.bookingInProgress.set(false);
    this.pendingCheckoutSessionId = null;
    this.selectionLocked.set(false);
  }

  public onInvoiceBookingConfirmed(): void {
    const bookedAddon = this.pendingInvoiceAddon;
    const quantity = this.pendingQuantity;
    this.pendingInvoiceAddon = null;
    this.selectionLocked.set(false);
    if (!bookedAddon) return;
    if (!this.bookingConfirmation.invoicePayment()) {
      this.bookingInProgress.set(true);
      this.bookingConfirmation.enableInvoicePayment().subscribe({
        next: () => this.changeExistingPlan(bookedAddon, quantity),
        error: () => this.bookingInProgress.set(false),
      });
      return;
    }
    this.changeExistingPlan(bookedAddon, quantity);
  }

  /**
   * Continues after the first-booking payment-method step.
   * @returns {void}
   */
  confirmPaymentMethod(): void {
    const choice = this.paymentMethodChoice();
    const bookedAddon = this.pendingInvoiceAddon;
    if (!choice || !bookedAddon || this.bookingInProgress()) {
      return;
    }
    this.bookingInProgress.set(true);
    choice.prepareSelection().subscribe({
      next: (method) => {
        this.bookingInProgress.set(false);
        this.isPaymentMethodStep.set(false);
        if (method === FirstBookingPaymentMethod.INVOICE) {
          this.openInvoiceConfirm(bookedAddon, this.pendingQuantity);
          return;
        }
        this.changeExistingPlan(bookedAddon, this.pendingQuantity);
      },
      error: () => this.bookingInProgress.set(false),
    });
  }

  /**
   * Immediate checkout vs period-end for the given slot count and amount.
   * @param {number} nextSlots Target agent seats
   * @param {number} nextAmountCents Target recurring amount in cents
   * @returns {boolean}
   */
  private isImmediate(nextSlots: number, nextAmountCents: number): boolean {
    if (!this.hasExistingAgentsSubscription()) return true;
    const currentInterval = this.currentAgentsInterval();
    if (!currentInterval) return true;
    return isImmediatePlanChange(
      currentInterval,
      this.currentMaxAgents(),
      this.billingInterval(),
      nextSlots,
      this.agentsSubscription()?.cost ?? 0,
      nextAmountCents,
    );
  }

  /**
   * Opens downgrade confirm when needed, otherwise books.
   * @param {StripeProduct} addon Catalog price
   * @param {number} quantity Agents 1er seat count
   * @returns {void}
   */
  private confirmOrBook(addon: StripeProduct, quantity: number): void {
    const maxAgents = this.maxAgentsOf(addon);
    const nextSlots = maxAgents === 1 ? quantity : maxAgents;
    if (
      this.hasExistingAgentsSubscription() &&
      !this.isImmediate(nextSlots, addon.unit_amount * quantity)
    ) {
      this.pendingDowngradeAddon = addon;
      this.pendingQuantity = quantity;
      this.selectionLocked.set(true);
      this.downgradeConfirmModal()?.modal.open();
      return;
    }
    this.bookAddon(addon, quantity);
  }

  /**
   * Starts invoice confirm or checkout / plan change.
   * @param {StripeProduct} bookedAddon Selected catalog price
   * @param {number} quantity Agents 1er seat count
   * @returns {void}
   */
  private bookAddon(bookedAddon: StripeProduct, quantity = 1): void {
    if (this.bookingInProgress()) return;
    this.pendingQuantity = Math.max(1, quantity);
    this.pendingInvoiceAddon = bookedAddon;
    if (this.bookingConfirmation.invoicePayment()) {
      this.openInvoiceConfirm(bookedAddon, this.pendingQuantity);
      return;
    }
    this.bookingInProgress.set(true);
    this.bookingConfirmation.resolveNeedsPaymentMethodChoice().subscribe({
      next: (needsChoice) => {
        this.bookingInProgress.set(false);
        if (needsChoice) {
          this.isPaymentMethodStep.set(true);
          return;
        }
        this.changeExistingPlan(bookedAddon, this.pendingQuantity);
      },
      error: () => {
        this.bookingInProgress.set(false);
        this.changeExistingPlan(bookedAddon, this.pendingQuantity);
      },
    });
  }

  /**
   * Opens the invoice booking confirmation for an Agents price.
   * @param {StripeProduct} bookedAddon Selected catalog price
   * @param {number} quantity Agents 1er seat count
   * @returns {void}
   */
  private openInvoiceConfirm(
    bookedAddon: StripeProduct,
    quantity: number,
  ): void {
    this.selectionLocked.set(true);
    this.invoiceConfirmModal().open(
      this.bookingConfirmation.toProductConfirmData(
        bookedAddon,
        translateBillingPeriod(
          this.transloco,
          bookedAddon,
          this.billingInterval(),
        ),
        quantity,
      ),
    );
  }

  /**
   * Books or changes Agents via change-subscription. Checkout only when Stripe
   * still needs a first payment method.
   * @param {StripeProduct} bookedAddon Selected agents price
   * @param {number} quantity Agents 1er seat count
   * @returns {void}
   */
  private changeExistingPlan(bookedAddon: StripeProduct, quantity = 1): void {
    this.bookingInProgress.set(true);
    const maxAgents = this.maxAgentsOf(bookedAddon);
    const agents = maxAgents === 1 ? quantity : maxAgents;

    this.stripeAdapter
      .changeSubscription(bookedAddon.id, window.location.href, { quantity })
      .subscribe({
        next: (result) => {
          if (result.needsCheckout && result.clientSecret) {
            this.pendingCheckoutSessionId = result.checkoutSessionId ?? null;
            this.onPaymentCompletedFunction.set(() => {
              this.bookingConfirmation.rememberPaymentMethodOnFile();
              this.stripeAdapter
                .applyBookedPrice(
                  bookedAddon.id,
                  quantity,
                  this.pendingCheckoutSessionId ?? undefined,
                )
                .subscribe({
                  next: () => this.finishSuccessfulBooking(agents, true),
                  error: (error) => {
                    console.error(
                      '[agents-booking-modal] applyBookedPrice after change failed',
                      error,
                    );
                    this.finishSuccessfulBooking(agents, true);
                  },
                });
            });
            this.isPaymentStep.set(true);
            this.mountChangeCheckout(
              result.clientSecret,
              result.checkoutSessionId,
            );
            return;
          }

          this.finishSuccessfulBooking(agents, !!result.upgraded);
        },
        error: (error) => {
          console.error(
            '[agents-booking-modal] changeSubscription failed',
            error,
          );
          this.toastsService.alert(
            'company-tool.views.settings.company.payment.sections.subscriptions.action-error',
          );
          this.bookingInProgress.set(false);
        },
      });
  }

  /**
   * Closes the modal after a successful Agents booking and shows a toast.
   * @param {number} agents Booked agent count
   * @param {boolean} [upgraded=true] False when the change is scheduled for period end
   * @returns {void}
   */
  private finishSuccessfulBooking(agents: number, upgraded = true): void {
    if (upgraded) {
      this.bookingConfirmation.showSuccessToast();
    } else {
      this.toastsService.info(
        'company-tool.snippets.agents-booking-modal.success-schedule',
      );
    }
    this.agentsBooked.emit({ agents });
    this.close();
    this.bookingInProgress.set(false);
  }

  /**
   * Loads Agents catalogs for both intervals.
   * @returns {void}
   */
  private loadProducts(): void {
    this.selfBookingAddonsLoaded.set(false);
    const requestId = ++this.catalogRequestId;
    this.stripeAdapter
      .getSelfBookingProducts(StripeProductType.AGENTS)
      .subscribe({
        next: (addons) => {
          if (requestId !== this.catalogRequestId) return;
          const priced = addons.filter((addon) => addon.unit_amount > 0);
          for (const interval of [
            StripeBillingInterval.MONTH,
            StripeBillingInterval.YEAR,
          ]) {
            this.catalogCache.set(
              interval,
              priced.filter((addon) => addon.recurring?.interval === interval),
            );
          }
          this.applyCatalog(
            this.billingInterval(),
            this.catalogCache.get(this.billingInterval()) ?? [],
          );
        },
        error: (error) => {
          if (requestId !== this.catalogRequestId) return;
          console.error(
            '[agents-booking-modal] load agents catalog failed',
            error,
          );
          this.selfBookingAddons.set([]);
          this.selfBookingAddonsLoaded.set(true);
        },
      });
  }

  /**
   * Applies a catalog and seeds selection from live priceId.
   * @param {StripeBillingInterval} interval Catalog interval
   * @param {StripeProduct[]} filtered Priced catalog entries
   * @returns {void}
   */
  private applyCatalog(
    interval: StripeBillingInterval,
    filtered: StripeProduct[],
  ): void {
    this.selfBookingAddons.set(filtered);
    const seat =
      filtered.find((addon) => this.maxAgentsOf(addon) === 1) ?? null;
    const priceId = this.currentPriceId();
    const onCurrentInterval = interval === this.currentAgentsInterval();

    if (this.selectedOptionValue() === null) {
      if (onCurrentInterval && priceId && seat?.id === priceId) {
        this.customAgentCount.set(this.currentQuantity());
        this.selectedOptionValue.set(this.CUSTOM_OPTION_VALUE);
      } else if (
        onCurrentInterval &&
        priceId &&
        filtered.some((addon) => addon.id === priceId)
      ) {
        this.selectedOptionValue.set(priceId);
      } else if (!this.hasExistingAgentsSubscription() && seat) {
        this.selectedOptionValue.set(this.CUSTOM_OPTION_VALUE);
      }
    }
    this.selfBookingAddonsLoaded.set(true);
  }

  private mountChangeCheckout(
    clientSecret: string,
    checkoutSessionId?: string | null,
  ): void {
    if (this.paymentCheckout()) {
      void this.paymentCheckout()?.mountFromClientSecret(
        clientSecret,
        checkoutSessionId,
      );
    } else {
      setTimeout(
        () => this.mountChangeCheckout(clientSecret, checkoutSessionId),
        10,
      );
    }
  }
}
